package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunWarningDto
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.percent

@Composable
fun DebugPanel(detail: StandupRunDetailDto) {
    val ui = rememberUiStore()
    val inputHash = remember(detail.rawLinearActivity) {
        detail.rawLinearActivity?.toString()?.let { simpleHash(it) } ?: "—"
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        if (detail.warnings.isNotEmpty()) {
            WarningsSection(detail.warnings)
        }
        PanelHeader("Operational metadata", topSpace = detail.warnings.isNotEmpty())
        DebugRow(label = "Standup ID", value = detail.id) { ui.copy(it) }
        DebugRow(label = "Standup ID", value = detail.standupId ?: "—") { ui.copy(it) }
        DebugRow(label = "Workspace ID", value = detail.workspaceId) { ui.copy(it) }
        DebugRow(label = "Source", value = detail.source.name.lowercase()) { ui.copy(it) }
        DebugRow(label = "Prompt version", value = detail.promptVersion ?: "—") {
            if (detail.promptVersion != null) ui.copy(it)
        }
        DebugRow(label = "Input hash", value = inputHash) { ui.copy(it) }
        detail.traceId?.let { DebugRow(label = "Trace ID", value = it) { ui.copy(it) } }
        DebugRow(label = "AI degraded", value = detail.aiDegraded.toString()) {}
        detail.errorCode?.let {
            DebugRow(label = "Error code", value = it) { ui.copy(it) }
        }
        detail.errorMessage?.let {
            DebugRow(label = "Error message", value = it) { ui.copy(it) }
        }

        PanelHeader("Timing", topSpace = true)
        DebugRow(
            label = "Scheduled for",
            value = formatAbsolute(detail.scheduledFor),
        ) { ui.copy(it) }
        detail.generationStartedAt?.let {
            DebugRow(label = "Generation started", value = formatAbsolute(it)) { ui.copy(it) }
        }
        detail.generationCompletedAt?.let {
            DebugRow(label = "Generation completed", value = formatAbsolute(it)) { ui.copy(it) }
        }
        detail.generationDurationMs?.let {
            DebugRow(label = "Duration", value = "${it}ms") { ui.copy(it) }
        }
        detail.postedAt?.let {
            DebugRow(label = "Posted at", value = formatAbsolute(it)) { ui.copy(it) }
        }

        PromptArchivePanel(workspaceId = detail.workspaceId, runId = detail.id)
    }
}

@Composable
private fun WarningsSection(warnings: List<StandupRunWarningDto>) {
    PanelHeader("Warnings (${warnings.size})")
    Column(
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "6px") },
    ) {
        warnings.forEach { w -> WarningRow(w) }
    }
}

@Composable
private fun WarningRow(w: StandupRunWarningDto) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.sm)
            .backgroundColor(Palette.surfaceSubtle)
            .styleModifier { property("gap", "10px") },
        verticalAlignment = Alignment.Top,
    ) {
        SpanText(
            text = "⚠",
            modifier = Modifier
                .fontSize(Typography.body.size)
                .color(Palette.warning),
        )
        Column(
            modifier = Modifier.styleModifier {
                property("flex", "1")
                property("min-width", "0")
            },
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .gap(Spacing.xs)
                    .styleModifier { property("flex-wrap", "wrap") },
            ) {
                SpanText(
                    text = w.code,
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary)
                        .styleModifier { property("font-family", Typography.FONT_MONO) },
                )
                SpanText(
                    text = formatAbsolute(w.at),
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary),
                )
            }
            SpanText(
                text = w.message,
                modifier = Modifier
                    .margin(top = Spacing.xxs)
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary)
                    .styleModifier { property("overflow-wrap", "anywhere") },
            )
        }
    }
}

@Composable
private fun PanelHeader(label: String, topSpace: Boolean = false) {
    SpanText(
        text = label,
        modifier = Modifier
            .margin(top = if (topSpace) Spacing.lg else Spacing.none, bottom = Spacing.xs)
            .fontSize(Typography.bodySmall.size)
            .fontWeight(600)
            .color(Palette.textSecondary),
    )
}

@Composable
private fun DebugRow(label: String, value: String, onCopy: (String) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs)
            .borderRadius(Radius.sm)
            .cursor(Cursor.Pointer)
            .onClick { onCopy(value) }
            .gap(Spacing.sm)
            .styleModifier {
                property("border-bottom", "1px solid ${surfaceSubtleRgb()}")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .width(35.percent)
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
        Box(
            modifier = Modifier
                .styleModifier {
                    property("flex", "1 1 0")
                    property("min-width", "0")
                    property("font-family", Typography.FONT_MONO)
                    property("overflow-wrap", "anywhere")
                },
        ) {
            SpanText(
                text = value,
                modifier = Modifier
                    .fontSize(Typography.code.size)
                    .color(Palette.textPrimary),
            )
        }
        Box(
            modifier = Modifier.styleModifier {
                property("flex", "0 0 auto")
                property("text-align", "right")
            },
        ) {
            SpanText(
                text = "copy",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textSecondary),
            )
        }
    }
}

private fun simpleHash(s: String): String {
    var h = 0L
    for (c in s) {
        h = (h * 31 + c.code) and 0xFFFFFFFFL
    }
    return h.toString(16).padStart(8, '0')
}

private fun copyToClipboard(value: String): Boolean {
    return try {
        val nav: dynamic = window.navigator
        val clip = nav.clipboard
        if (clip != null && clip != js("undefined")) {
            clip.writeText(value)
            true
        } else false
    } catch (_: Throwable) {
        false
    }
}

private fun co.maytes.standupbot.dashboard.stores.UiContainer.copy(value: String) {
    val ok = copyToClipboard(value)
    showToast(
        if (ok) "Copied to clipboard" else "Copy not supported",
        if (ok) ToastKind.Success else ToastKind.Error,
    )
}

internal fun surfaceSubtleRgb(): String {
    val rgb = Palette.surfaceSubtle as? com.varabyte.kobweb.compose.ui.graphics.Color.Rgb ?: return "currentColor"
    val r = (rgb.value shr 16) and 0xFF
    val g = (rgb.value shr 8) and 0xFF
    val b = rgb.value and 0xFF
    return "rgb($r, $g, $b)"
}
